import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteApi, getApi, postApi, putApi } from '../../services/apiServices';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function AdminBlog() {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddBlogModal, setShowAddBlogModal] = useState(false);
    const [showEditBlogModal, setShowEditBlogModal] = useState(false);
    const [showDeleteBlogModal, setShowDeleteBlogModal] = useState(false);
    const [blogForm, setBlogForm] = useState({
        title: '',
        description: '',
        author: '',
        status: true,
        image: null
    });
    const [editBlogForm, setEditBlogForm] = useState({
        id: null,
        title: '',
        description: '',
        author: '',
        status: true,
        image: null
    });
    const [deleteBlogData, setDeleteBlogData] = useState(null);
    const [blogLoading, setBlogLoading] = useState(false);
    const [editBlogLoading, setEditBlogLoading] = useState(false);
    const [deleteBlogLoading, setDeleteBlogLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [editImagePreview, setEditImagePreview] = useState(null);

    // Modal styles (copied from customers page)
    const customStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            border: 'none',
            borderRadius: '10px',
            padding: '0',
            maxWidth: '800px',
            width: '90%',
            backgroundColor: 'transparent'
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
        }
    };

    const fetchBlogs = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await getApi('/api/blogs', { showSuccessToast: false });

            if (response?.status) {
                setBlogs(response.data?.blogs || []);
            } else {
                setError(response?.message || 'Failed to fetch blogs');
                toast.error(response?.message || 'Failed to fetch blogs');
            }
        } catch (error) {
            console.error('Error fetching blogs:', error);
            setError('Failed to fetch blogs. Please try again.');
            toast.error('Failed to fetch blogs. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    };

    const openAddBlogModal = () => {
        setShowAddBlogModal(true);
    };

    const closeAddBlogModal = () => {
        if (blogLoading) return;
        setShowAddBlogModal(false);
        setBlogForm({
            title: '',
            description: '',
            author: '',
            status: true,
            image: null
        });
        setImagePreview(null);
    };

    const openEditBlogModal = (blog) => {
        setEditBlogForm({
            id: blog.id,
            title: blog.title || '',
            description: blog.description || '',
            author: blog.author || '',
            status: blog.status || false,
            image: null
        });
        setEditImagePreview(blog.image_full_url || null);
        setShowEditBlogModal(true);
    };

    const closeEditBlogModal = () => {
        if (editBlogLoading) return;
        setShowEditBlogModal(false);
        setEditBlogForm({
            id: null,
            title: '',
            description: '',
            author: '',
            status: true,
            image: null
        });
        setEditImagePreview(null);
    };

    const openViewBlogModal = (blog) => {
        navigate(`/admin/viewBlog/${blog.id}`);
    };


    const openDeleteBlogModal = (blog) => {
        setDeleteBlogData(blog);
        setShowDeleteBlogModal(true);
    };

    const closeDeleteBlogModal = () => {
        if (deleteBlogLoading) return;
        setShowDeleteBlogModal(false);
        setDeleteBlogData(null);
    };

    const handleBlogFormChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'file') {
            const file = files[0];
            if (file) {
                setBlogForm(prev => ({
                    ...prev,
                    image: file
                }));

                const reader = new FileReader();
                reader.onload = (e) => {
                    setImagePreview(e.target.result);
                };
                reader.readAsDataURL(file);
            }
        } else if (type === 'checkbox') {
            setBlogForm(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setBlogForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleEditBlogFormChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'file') {
            const file = files[0];
            if (file) {
                setEditBlogForm(prev => ({
                    ...prev,
                    image: file
                }));

                const reader = new FileReader();
                reader.onload = (e) => {
                    setEditImagePreview(e.target.result);
                };
                reader.readAsDataURL(file);
            }
        } else if (type === 'checkbox') {
            setEditBlogForm(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setEditBlogForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const stripHtmlTags = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const getCharacterCount = (html) => {
        return stripHtmlTags(html).length;
    };

    const handleCreateBlog = async () => {
        try {
            setBlogLoading(true);

            if (!blogForm.title.trim()) {
                toast.error('Please enter a title');
                return;
            }
            if (!blogForm.description.trim()) {
                toast.error('Please enter a description');
                return;
            }
            if (!blogForm.author.trim()) {
                toast.error('Please enter an author');
                return;
            }
            if (blogForm.title.length > 200) {
                toast.error('Title must be 200 characters or less');
                return;
            }
            if (getCharacterCount(blogForm.description) > 2000) {
                toast.error('Description must be 2000 characters or less (excluding HTML tags)');
                return;
            }

            const formData = new FormData();
            formData.append('title', blogForm.title.trim());
            formData.append('description', blogForm.description.trim());
            formData.append('author', blogForm.author.trim());
            formData.append('status', blogForm.status ? 1 : 0);

            if (blogForm.image) {
                formData.append('image', blogForm.image);
            }

            const response = await postApi('/api/blogs', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                showSuccessToast: false
            });

            if (response?.status) {
                toast.success(response.message || 'Blog created successfully');
                closeAddBlogModal();
                fetchBlogs(); 
            } else {
                toast.error(response?.message || 'Failed to create blog');
            }
        } catch (error) {
            console.error('Error creating blog:', error);
            toast.error(error?.message || 'Failed to create blog. Please try again.');
        } finally {
            setBlogLoading(false);
        }
    };

    const handleUpdateBlog = async () => {
        try {
            setEditBlogLoading(true);

            if (!editBlogForm.title.trim()) {
                toast.error('Please enter a title');
                return;
            }
            if (!editBlogForm.description.trim()) {
                toast.error('Please enter a description');
                return;
            }
            if (!editBlogForm.author.trim()) {
                toast.error('Please enter an author');
                return;
            }
            if (editBlogForm.title.length > 200) {
                toast.error('Title must be 200 characters or less');
                return;
            }
            if (getCharacterCount(editBlogForm.description) > 2000) {
                toast.error('Description must be 2000 characters or less (excluding HTML tags)');
                return;
            }
            const formData = new FormData();
            formData.append('title', editBlogForm.title.trim());
            formData.append('description', editBlogForm.description.trim());
            formData.append('author', editBlogForm.author.trim());
            formData.append('status', editBlogForm.status ? 1 : 0);

            if (editBlogForm.image && editBlogForm.image instanceof File) {
                formData.append('image', editBlogForm.image);
            }

            const response = await postApi(`/api/blogs/${editBlogForm.id}?_method=PUT`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                showSuccessToast: false
            });

            if (response?.status) {
                toast.success(response.message || 'Blog updated successfully');
                closeEditBlogModal();
                fetchBlogs(); 
            } else {
                toast.error(response?.message || 'Failed to update blog');
            }
        } catch (error) {
            console.error('Error updating blog:', error);
            toast.error(error?.message || 'Failed to update blog. Please try again.');
        } finally {
            setEditBlogLoading(false);
        }
    };

    const handleDeleteBlog = async () => {
        try {
            setDeleteBlogLoading(true);

            if (!deleteBlogData?.id) {
                toast.error('Blog ID not found');
                return;
            }

            const response = await deleteApi(`/api/blogs/${deleteBlogData.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (data?.status) {
                toast.success(data.message || 'Blog deleted successfully');
                closeDeleteBlogModal();
                fetchBlogs(); 
            } else {
                toast.error(data?.message || 'Failed to delete blog');
            }
        } catch (error) {
            console.error('Error deleting blog:', error);
            toast.error('Failed to delete blog. Please try again.');
        } finally {
            setDeleteBlogLoading(false);
        }
    };

    useEffect(() => {
        fetchBlogs();
    }, []);

    return (
        <>
            <div className="right-section blog-page main-page-cs">
                <div className="customer-top">
                    <div className="customer-top-left">
                        {/* <h2>Blogs</h2> */}
                    </div>
                    <div className="customer-top-right">
                        <button onClick={openAddBlogModal} className="btn add-btns">
                            Add New Blog
                        </button>
                    </div>
                </div>

                <div className="customer-tablle-main">
                    {loading ? (
                        <div className="loader-overlay">
                            <span className="loader"></span>
                        </div>
                    ) : error ? (
                        <div className="error-container">
                            <p>{error}</p>
                            <button onClick={fetchBlogs} className="retry-btn">
                                Retry
                            </button>
                        </div>
                    ) : (
                        <table className="table">
                            <thead>
                                <tr>
                                    <th scope="col" className='Sr-no' >Sr No.</th>
                                    <th scope="col" className='title'>Title</th>
                                    <th scope="col" className='author'>Author</th>
                                    <th scope="col" className='status'>Status</th>
                                    <th scope="col" className='created'>Created At</th>
                                    {/* <th scope="col" className='image'>Image</th> */}
                                    <th scope="col" className='action'>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {blogs.length > 0 ? (
                                    blogs.map((blog, index) => (
                                        <tr key={blog.id || index}>
                                            <td className='Sr-no'>{index + 1}</td>
                                            <td className='title'>{blog.title || 'N/A'}</td>
                                            <td className='author'>{blog.author || 'N/A'}</td>
                                            <td className='status'>
                                                <span className={`status-badge ${blog.status ? 'active' : 'inactive'}`}>
                                                    {blog.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className='created'>{formatDate(blog.created_at)}</td>
                                            {/* <td className='image'>
                                                <span>
                                                    {blog.image_url && (
                                                        <img
                                                            src={blog.image_full_url}
                                                            alt={blog.title}
                                                        />
                                                    )}
                                                </span>
                                            </td> */}
                                            <td className='action'>
                                                <div className="dropdown drop-down-action">
                                                    <button
                                                        className="btn action-btn dropdown-toggle"
                                                        type="button"
                                                        id={`dropdownMenuButton-${index}`}
                                                        data-toggle="dropdown"
                                                        aria-haspopup="true"
                                                        aria-expanded="false"
                                                    >
                                                        <img src={window.getPublicUrl('admin/images/action.svg')} alt="action" />
                                                    </button>
                                                    <div className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${index}`}>
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => openViewBlogModal(blog)}
                                                        >
                                                            <img src={window.getPublicUrl('admin/images/view.svg')} alt="view" className='pr-2' /> View
                                                        </button>
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => openEditBlogModal(blog)}
                                                        >
                                                            <img src={window.getPublicUrl('admin/images/edit.svg')} alt="edit" className='pr-2' /> Edit
                                                        </button>
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => openDeleteBlogModal(blog)}
                                                        >
                                                            <img src={window.getPublicUrl('admin/images/delete.png')} alt="view" className='pr-2' /> Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="no-data text-center">
                                            No blogs found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Add Blog Modal */}
            <Modal
                isOpen={showAddBlogModal}
                onRequestClose={blogLoading ? undefined : closeAddBlogModal}
                style={customStyles}
                contentLabel="Add Blog Modal"
            >
                <div className="paymentsuccess add-blog-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Add New Blog</h5>
                            <button
                                type="button"
                                className="close"
                                onClick={closeAddBlogModal}
                                aria-label="Close"
                                disabled={blogLoading}
                            >
                                <img src={window.getPublicUrl('admin/images/close-icon.svg')} alt="" />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Photo</label>
                                        <div
                                            className="custom-upload-area"
                                            onClick={() => document.getElementById('imageUpload').click()}
                                        >
                                            <input
                                                type="file"
                                                id="imageUpload"
                                                className="d-none"
                                                accept="image/*"
                                                name="image"
                                                onChange={handleBlogFormChange}
                                            />
                                            {imagePreview ? (
                                                //  <img   alt="Preview" src={window.getPublicUrl('admin/images/close-icon.svg')} style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }} />
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    style={{ maxHeight: '180px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="upload-placeholder">
                                                    <img alt="Preview" src={window.getPublicUrl('admin/images/upload-icon.svg')} style={{ maxWidth: '200px', maxHeight: '200px', height: '50px', objectFit: 'cover' }} />

                                                    {/* <img
                                            src="/path/to/upload-icon.png" // Replace with your icon
                                            alt="Upload Icon"
                                            style={{ width: '30px', marginBottom: '10px' }}
                                        /> */}
                                                    <p>Choose Image</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Title <span className="text-muted">({blogForm.title.length}/200 words)</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter blog title"
                                            name="title"
                                            value={blogForm.title}
                                            onChange={handleBlogFormChange}
                                            maxLength={200}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Description <span className="text-muted">({getCharacterCount(blogForm.description)}/2000 characters)</span></label>
                                        <ReactQuill
                                            theme="snow"
                                            value={blogForm.description}
                                            onChange={(content) => {
                                                setBlogForm(prev => ({
                                                    ...prev,
                                                    description: content
                                                }));
                                            }}
                                            placeholder="Enter blog description"
                                            modules={{
                                                toolbar: [
                                                    [{ 'header': [1, 2, 3, false] }],
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'color': [] }, { 'background': [] }],
                                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                    [{ 'align': [] }],
                                                    ['link', 'image'],
                                                    ['clean']
                                                ]
                                            }}
                                            style={{ height: '200px', marginBottom: '50px' }}
                                        />
                                        {/* <small className="form-text text-muted">
                                            You can use HTML tags for formatting (e.g., &lt;b&gt;, &lt;i&gt;, &lt;br&gt;, &lt;p&gt;)
                                        </small> */}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>Author</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter author name"
                                            name="author"
                                            value={blogForm.author}
                                            onChange={handleBlogFormChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>Status</label>
                                        <div className="custom-control custom-switch">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="blogStatus"
                                                name="status"
                                                checked={blogForm.status}
                                                onChange={handleBlogFormChange}
                                            />
                                            <label className="custom-control-label" htmlFor="blogStatus">
                                                {blogForm.status ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-cancel" onClick={closeAddBlogModal} disabled={blogLoading}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-save"
                                onClick={handleCreateBlog}
                                disabled={blogLoading}
                            >
                                {blogLoading ? 'Creating...' : 'Create Blog'}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Edit Blog Modal */}
            <Modal
                isOpen={showEditBlogModal}
                onRequestClose={editBlogLoading ? undefined : closeEditBlogModal}
                style={customStyles}
                contentLabel="Edit Blog Modal"
            >
                <div className=" add-blog-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Blog</h5>
                            <button
                                type="button"
                                className="close"
                                onClick={closeEditBlogModal}
                                aria-label="Close"
                                disabled={editBlogLoading}
                            >
                                <img src={window.getPublicUrl('admin/images/close-icon.svg')} alt="" />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Photo</label>
                                        <div className="custom-upload-area" onClick={() => document.getElementById('editImageUpload').click()}>
                                            <input
                                                type="file"
                                                id="editImageUpload"
                                                className="d-none"
                                                accept="image/*"
                                                name="image"
                                                onChange={handleEditBlogFormChange}
                                            />
                                            {editImagePreview ? (
                                                <img
                                                    src={editImagePreview}
                                                    alt="Preview"
                                                    style={{ maxHeight: '180px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="upload-placeholder">
                                                    <img src="upload-icon.png" alt="Upload Icon" style={{ width: '180px', marginBottom: '180px', }} />
                                                    <p>Choose Image</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Title <span className="text-muted">({editBlogForm.title.length}/200 words)</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter blog title"
                                            name="title"
                                            value={editBlogForm.title}
                                            onChange={handleEditBlogFormChange}
                                            maxLength={200}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Description <span className="text-muted">({getCharacterCount(editBlogForm.description)}/2000 characters)</span></label>
                                        <ReactQuill
                                            theme="snow"
                                            value={editBlogForm.description}
                                            onChange={(content) => {
                                                setEditBlogForm(prev => ({
                                                    ...prev,
                                                    description: content
                                                }));
                                            }}
                                            placeholder="Enter blog description"
                                            modules={{
                                                toolbar: [
                                                    [{ 'header': [1, 2, 3, false] }],
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'color': [] }, { 'background': [] }],
                                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                    [{ 'align': [] }],
                                                    ['link', 'image'],
                                                    ['clean']
                                                ]
                                            }}
                                            style={{ height: '200px', marginBottom: '50px' }}
                                        />
                                        {/* <small className="form-text text-muted">
                                            You can use HTML tags for formatting 
                                        </small> */}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>Author</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter author name"
                                            name="author"
                                            value={editBlogForm.author}
                                            onChange={handleEditBlogFormChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>Status</label>
                                        <div className="custom-control custom-switch">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="editBlogStatus"
                                                name="status"
                                                checked={editBlogForm.status}
                                                onChange={handleEditBlogFormChange}
                                            />
                                            <label className="custom-control-label" htmlFor="editBlogStatus">
                                                {editBlogForm.status ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-cancel" onClick={closeEditBlogModal} disabled={editBlogLoading}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-save"
                                onClick={handleUpdateBlog}
                                disabled={editBlogLoading}
                            >
                                {editBlogLoading ? 'Updating...' : 'Update Blog'}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Delete Blog Modal */}
            <Modal
                isOpen={showDeleteBlogModal}
                onRequestClose={deleteBlogLoading ? undefined : closeDeleteBlogModal}
                style={customStyles}
                contentLabel="Delete Blog Modal"
            >
                <div className="paymentsuccess add-blog-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Blog</h5>
                            <button
                                type="button"
                                className="close"
                                onClick={closeDeleteBlogModal}
                                aria-label="Close"
                                disabled={deleteBlogLoading}
                            >
                                <img src={window.getPublicUrl('admin/images/close-icon.svg')} alt="" />
                            </button>
                        </div>
                        <div className="modal-body">
                            {deleteBlogData && (
                                <div className="text-center">
                                   
                                    <h4 className="mb-3">Are you sure you want to delete this blog?</h4>

                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-cancel" onClick={closeDeleteBlogModal} disabled={deleteBlogLoading}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-save"
                                onClick={handleDeleteBlog}
                                disabled={deleteBlogLoading}
                            >
                                {deleteBlogLoading ? 'Deleting...' : 'Delete Blog'}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default AdminBlog;